import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  Clock,
  Calendar,
  BookOpen,
  User,
  PlusCircle,
  FileText,
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  CalendarDays,
  GraduationCap,
  IndianRupee,
  MapPin,
  Users,
  Sparkles
} from 'lucide-react';

const TeacherDashboard = () => {
  const { user, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [teacherData, setTeacherData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [todayClasses, setTodayClasses] = useState([]);
  const [todayHolidays, setTodayHolidays] = useState([]);
  const [upcomingStaffHolidays, setUpcomingStaffHolidays] = useState([]);
  const [teachingHours, setTeachingHours] = useState({ current: 0, previous: 0 });
  const [leaveStats, setLeaveStats] = useState({ taken: 0, available: 0, pending: 0 });
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);

  // Leave form state
  const [leaveForm, setLeaveForm] = useState({
    from_date: '',
    to_date: '',
    leave_type: 'Full Day',
    reason: '',
    days_count: 0
  });

  // Session form state
  const [sessionForm, setSessionForm] = useState({
    subject_id: '',
    batch_id: '',
    classroom_id: '',
    date: '',
    start_time: '',
    end_time: ''
  });

  const [subjects, setSubjects] = useState([]);
  const [batches, setBatches] = useState([]);
  const [classrooms, setClassrooms] = useState([]);

  const BACKEND_URL = getBackendUrl();

  // Get current month and previous month
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  const getPreviousMonth = () => {
    const now = new Date();
    const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    return `${year}-${(prevMonth + 1).toString().padStart(2, '0')}`;
  };

  const formatMonthLabel = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Calculate leave days including sandwich leaves
  const calculateLeaveDays = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 0;
    
    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    
    if (endDate < startDate) return 0;
    
    const timeDifference = endDate.getTime() - startDate.getTime();
    const totalDays = Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1;
    
    let currentDate = new Date(startDate);
    let workingDays = 0;
    let weekendDays = 0;
    
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      if (isWeekend) {
        weekendDays++;
      } else {
        workingDays++;
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // For long leaves or potential sandwich leaves, include weekends
    if (totalDays >= 5 || (totalDays > 2 && weekendDays > 0)) {
      return workingDays + weekendDays;
    }
    
    return workingDays;
  };

  // Auto-calculate leave days when dates change
  useEffect(() => {
    if (leaveForm.from_date && leaveForm.to_date) {
      const calculatedDays = calculateLeaveDays(leaveForm.from_date, leaveForm.to_date);
      setLeaveForm(prev => ({
        ...prev,
        days_count: calculatedDays
      }));
    }
  }, [leaveForm.from_date, leaveForm.to_date]);

  // Load teacher data
  const loadTeacherData = async () => {
    try {
      // Get teacher ID from URL params (for admin viewing) or use current user
      const teacherId = searchParams.get('teacherId');
      
      const teachersResponse = await axios.get(`${BACKEND_URL}/api/teachers`);
      let teacher;
      
      if (teacherId && isAdmin()) {
        // Admin viewing specific teacher dashboard
        teacher = teachersResponse.data.find(t => t.id === teacherId);
      } else {
        // Current user viewing their own dashboard
        teacher = teachersResponse.data.find(t => t.email === user.email);
      }
      
      if (teacher) {
        setTeacherData(teacher);
        
        // Load employee data to get assigned subjects
        await loadEmployeeData(teacher.id);
        
        // Load today's classes for this teacher
        await loadTodayClasses(teacher.id);
        
        // Load today's holidays
        await loadTodayHolidays();
        await loadUpcomingStaffHolidays();
        
        // Load teaching hours
        await loadTeachingHours(teacher.id);
        
        // Load leave statistics
        await loadLeaveStats(teacher.id);
      }
    } catch (error) {
      console.error('Error loading teacher data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  // Load employee data to get assigned subjects
  const loadEmployeeData = async (teacherId) => {
    try {
      const token = localStorage.getItem('token');
      const employeesResponse = await axios.get(`${BACKEND_URL}/api/payroll/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // For non-admin users, the API returns only their own employee record
      // For admin users, we need to find by teacher_id
      const employee = employeesResponse.data.length === 1 
        ? employeesResponse.data[0]  // Non-admin: use the only record returned
        : employeesResponse.data.find(emp => emp.teacher_id === teacherId);  // Admin: find by teacher_id
        
      if (employee) {
        setEmployeeData(employee);
        
        // Load subject details for assigned subjects
        if (employee.subjects && employee.subjects.length > 0) {
          const subjectsResponse = await axios.get(`${BACKEND_URL}/api/subjects`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const employeeSubjects = subjectsResponse.data.filter(subject => 
            employee.subjects.includes(subject.id)
          );
          setAssignedSubjects(employeeSubjects);
        } else {
          // No subjects assigned
          setAssignedSubjects([]);
        }
      } else {
        console.log('No employee record found for teacher:', teacherId);
        setAssignedSubjects([]);
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      setAssignedSubjects([]);
    }
  };

  // Load today's classes
  const loadTodayClasses = async (teacherId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      // Use date filter to get only today's sessions (more efficient and reliable)
      const sessionsResponse = await axios.get(`${BACKEND_URL}/api/sessions`, {
        params: {
          date_from: today,
          date_to: today,
          teacher_id: teacherId
        }
      });
      
      const todaySessions = sessionsResponse.data.filter(session => {
        return ['scheduled', 'in-progress'].includes(session.status);
      });
      
      setTodayClasses(todaySessions);
    } catch (error) {
      console.error('Error loading today\'s classes:', error);
    }
  };

  // Load teaching hours
  const loadTeachingHours = async (teacherId) => {
    try {
      // Find employee record for this teacher
      const employeesResponse = await axios.get(`${BACKEND_URL}/api/payroll/employees`);
      const employee = employeesResponse.data.find(emp => emp.teacher_id === teacherId);
      
      if (employee) {
        const currentMonth = getCurrentMonth();
        const previousMonth = getPreviousMonth();
        
        let currentHours = 0;
        let previousHours = 0;
        
        try {
          const currentResponse = await axios.get(`${BACKEND_URL}/api/payroll/teaching-hours/${employee.id}?month=${currentMonth}`);
          if (currentResponse.data && currentResponse.data.length > 0) {
            currentHours = currentResponse.data[0].total_hours || 0;
          }
        } catch (error) {
          // No hours recorded for current month
        }
        
        try {
          const previousResponse = await axios.get(`${BACKEND_URL}/api/payroll/teaching-hours/${employee.id}?month=${previousMonth}`);
          if (previousResponse.data && previousResponse.data.length > 0) {
            previousHours = previousResponse.data[0].total_hours || 0;
          }
        } catch (error) {
          // No hours recorded for previous month
        }
        
        setTeachingHours({ current: currentHours, previous: previousHours });
      }
    } catch (error) {
      console.error('Error loading teaching hours:', error);
    }
  };

  // Load today's holidays
  const loadTodayHolidays = async () => {
    try {
      const today = new Date();
      const currentYear = today.getFullYear();
      const todayString = today.toISOString().split('T')[0];
      
      const holidaysResponse = await axios.get(`${BACKEND_URL}/api/holidays?year=${currentYear}`);
      
      const todaysHolidays = holidaysResponse.data.filter(holiday => {
        return holiday.date === todayString;
      });
      
      setTodayHolidays(todaysHolidays);
    } catch (error) {
      console.error('Error fetching today\'s holidays:', error);
    }
  };

  const loadUpcomingStaffHolidays = async () => {
    try {
      const today = new Date();
      const currentYear = today.getFullYear();
      const todayString = today.toISOString().split('T')[0];
      
      // Get next 30 days of staff holidays
      const thirtyDaysLater = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
      const thirtyDaysString = thirtyDaysLater.toISOString().split('T')[0];
      
      const holidaysResponse = await axios.get(`${BACKEND_URL}/api/holidays?year=${currentYear}`);
      
      const upcomingStaffHolidays = holidaysResponse.data.filter(holiday => {
        return holiday.is_staff_holiday && 
               holiday.date > todayString && 
               holiday.date <= thirtyDaysString;
      }).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setUpcomingStaffHolidays(upcomingStaffHolidays);
    } catch (error) {
      console.error('Error fetching upcoming staff holidays:', error);
    }
  };

  // Load leave statistics
  const loadLeaveStats = async (teacherId) => {
    try {
      // Find employee record for this teacher
      const employeesResponse = await axios.get(`${BACKEND_URL}/api/payroll/employees`);
      const employee = employeesResponse.data.find(emp => emp.teacher_id === teacherId);
      
      if (employee) {
        // Get leave requests for this employee
        const leaveResponse = await axios.get(`${BACKEND_URL}/api/payroll/leave-requests`);
        const employeeLeaves = leaveResponse.data.filter(leave => leave.employee_id === employee.id);
        
        const currentYear = new Date().getFullYear();
        const yearlyLeaves = employeeLeaves.filter(leave => 
          new Date(leave.from_date).getFullYear() === currentYear
        );
        
        const approvedLeaves = yearlyLeaves.filter(leave => leave.status === 'approved');
        const pendingLeaves = yearlyLeaves.filter(leave => leave.status === 'pending');
        
        const totalTaken = approvedLeaves.reduce((sum, leave) => sum + leave.days_count, 0);
        const available = employee.leave_policy?.paid_leaves_per_month * 12 || 24; // Default 24 per year
        
        setLeaveStats({
          taken: totalTaken,
          available: available,
          pending: pendingLeaves.length
        });
      }
    } catch (error) {
      console.error('Error loading leave stats:', error);
    }
  };

  // Load dropdown data
  const loadDropdownData = async () => {
    try {
      const [subjectsRes, batchesRes, classroomsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subjects`),
        axios.get(`${BACKEND_URL}/api/batches`),
        axios.get(`${BACKEND_URL}/api/classrooms`)
      ]);
      
      setSubjects(subjectsRes.data);
      setBatches(batchesRes.data);
      setClassrooms(classroomsRes.data);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  // Submit leave request
  const submitLeaveRequest = async () => {
    try {
      setLoading(true);
      
      if (!teacherData) {
        toast.error('Teacher data not loaded');
        return;
      }
      
      // Find employee record
      // For non-admin users, API returns only their own employee record
      const employeesResponse = await axios.get(`${BACKEND_URL}/api/payroll/employees`);
      
      const employee = employeesResponse.data.length === 1
        ? employeesResponse.data[0]  // Non-admin: use the only record returned
        : employeesResponse.data.find(emp => emp.teacher_id === teacherData.id);  // Admin: find by teacher_id
      
      if (!employee) {
        toast.error('Employee record not found. Please contact admin.');
        console.error('Employee lookup failed. teacherData:', teacherData, 'API response:', employeesResponse.data);
        return;
      }
      
      console.log('Found employee for leave request:', employee);
      
      const requestData = {
        employee_id: employee.id,
        ...leaveForm,
        affected_classes: [] // Would be populated by affected classes logic
      };
      
      await axios.post(`${BACKEND_URL}/api/payroll/leave-requests`, requestData);
      toast.success('Leave request submitted successfully');
      
      setShowLeaveModal(false);
      setLeaveForm({
        from_date: '',
        to_date: '',
        leave_type: 'Full Day',
        reason: '',
        days_count: 0
      });
      
      // Reload leave stats
      await loadLeaveStats(teacherData.id);
      
    } catch (error) {
      console.error('Error submitting leave request:', error);
      const errorMessage = error.response?.data?.detail 
        ? (Array.isArray(error.response.data.detail) 
          ? error.response.data.detail.map(e => e.msg).join(', ')
          : error.response.data.detail)
        : 'Failed to submit leave request';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Submit session request
  const submitSessionRequest = async () => {
    try {
      setLoading(true);
      
      const sessionData = {
        ...sessionForm,
        teacher_id: teacherData.id,
        status: 'scheduled'
      };
      
      await axios.post(`${BACKEND_URL}/api/sessions`, sessionData);
      toast.success('Session scheduled successfully');
      
      setShowSessionModal(false);
      setSessionForm({
        subject_id: '',
        batch_id: '',
        classroom_id: '',
        date: '',
        start_time: '',
        end_time: ''
      });
      
      // Reload today's classes if the scheduled session is for today
      if (sessionForm.date === new Date().toISOString().split('T')[0]) {
        await loadTodayClasses(teacherData.id);
      }
      
    } catch (error) {
      console.error('Error scheduling session:', error);
      toast.error('Failed to schedule session');
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        loadTeacherData(),
        loadDropdownData()
      ]);
      setLoading(false);
    };
    
    if (user?.email) {
      loadData();
    }
  }, [user]);

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              {searchParams.get('teacherId') && isAdmin() 
                ? `${teacherData?.name || searchParams.get('teacherName')}'s Dashboard 📊`
                : `Welcome, ${teacherData?.name || user?.name}! 👋`
              }
            </h1>
            <p className="text-blue-100 mt-1">
              {searchParams.get('teacherId') && isAdmin()
                ? `Teaching overview and performance metrics`
                : `Here's your teaching overview for today`
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">Today</p>
            <p className="text-xl font-semibold">{new Date().toLocaleDateString('en-IN', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {/* Today's Classes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Classes</CardTitle>
            <Calendar className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{todayClasses.length}</div>
            <p className="text-xs text-gray-600">
              {todayClasses.length === 0 ? 'No classes today' : 'Classes scheduled'}
            </p>
          </CardContent>
        </Card>

        {/* Teaching Hours This Month */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{formatMonthLabel(getCurrentMonth())}</CardTitle>
            <Clock className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{teachingHours.current}h</div>
            <p className="text-xs text-gray-600">
              {teachingHours.current > teachingHours.previous 
                ? `+${teachingHours.current - teachingHours.previous}h from last month`
                : `${teachingHours.previous - teachingHours.current}h less than last month`
              }
            </p>
          </CardContent>
        </Card>

        {/* Assigned Subjects */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Assigned Subjects</CardTitle>
            <GraduationCap className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-indigo-600">{assignedSubjects.length}</div>
            <p className="text-xs text-gray-600">
              {assignedSubjects.length === 0 ? 'No subjects assigned' : 'Subjects teaching'}
            </p>
            {assignedSubjects.length > 0 && (
              <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                <div className="flex flex-wrap gap-1">
                  {assignedSubjects.map(subject => (
                    <Badge key={subject.id} variant="outline" className="text-xs">
                      {subject.name.length > 25 ? subject.name.substring(0, 25) + '...' : subject.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaves Taken This Year */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Leaves Taken This Year</CardTitle>
            <FileText className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {leaveStats.taken}
            </div>
            <p className="text-xs text-gray-600">
              {leaveStats.available - leaveStats.taken} remaining • {leaveStats.pending} pending
            </p>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
            <Target className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full text-xs"
              onClick={() => setShowLeaveModal(true)}
            >
              Apply Leave
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full text-xs"
              onClick={() => setShowSessionModal(true)}
            >
              Schedule Session
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Today's Schedule */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Classes Schedule */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
              Today's Class Schedule
              {todayHolidays.length > 0 && (
                <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-700">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Holiday
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Show holidays first if any */}
            {todayHolidays.length > 0 && (
              <div className="mb-4 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <Sparkles className="w-5 h-5 text-orange-600 mr-2" />
                  <h4 className="font-semibold text-orange-800">Today's Holiday{todayHolidays.length > 1 ? 's' : ''}</h4>
                </div>
                {todayHolidays.map((holiday, index) => (
                  <div key={holiday.id} className="mb-2 last:mb-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-orange-900">{holiday.name}</p>
                          {holiday.is_staff_holiday && (
                            <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                              <Sparkles className="w-2 h-2 mr-1" />
                              Day Off
                            </Badge>
                          )}
                        </div>
                        {holiday.description && (
                          <p className="text-sm text-orange-700">{holiday.description}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-orange-700 border-orange-300 capitalize">
                        {holiday.holiday_type}
                      </Badge>
                    </div>
                  </div>
                ))}
                <p className="text-sm text-orange-600 mt-2 italic">
                  {todayHolidays.some(h => h.is_staff_holiday) 
                    ? (todayClasses.length > 0 
                       ? `You have ${todayClasses.length} class${todayClasses.length > 1 ? 'es' : ''} on staff holiday - check with admin.`
                       : 'Staff holiday - Enjoy your day off!')
                    : (todayClasses.length > 0 
                       ? `You still have ${todayClasses.length} class${todayClasses.length > 1 ? 'es' : ''} scheduled.`
                       : 'No classes scheduled - enjoy the holiday!')
                  }
                </p>
              </div>
            )}

            {todayClasses.length === 0 && todayHolidays.length === 0 ? (
              <div className="text-center py-8">
                <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No classes scheduled for today</p>
                <p className="text-sm text-gray-400 mt-1">Enjoy your free day!</p>
              </div>
            ) : todayClasses.length > 0 ? (
              <div className="space-y-3">
                {todayClasses.map((session, index) => (
                  <div key={session.id} className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{session.subject_name}</h4>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {session.time_slot}
                          </div>
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {session.classroom_name}
                          </div>
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {session.batch_name}
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-green-700 border-green-300">
                        {session.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Performance Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
              Performance Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Teaching Hours</span>
                <span className="text-sm text-gray-600">{teachingHours.current}h / Month</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{width: `${Math.min((teachingHours.current / 50) * 100, 100)}%`}}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Leave Usage</span>
                <span className="text-sm text-gray-600">
                  {leaveStats.taken} / {leaveStats.available}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-600 h-2 rounded-full" 
                  style={{width: `${Math.min((leaveStats.taken / leaveStats.available) * 100, 100)}%`}}
                ></div>
              </div>
            </div>

            <div className="pt-3 border-t">
              <div className="flex items-center text-sm">
                {leaveStats.pending > 0 ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-orange-500 mr-2" />
                    <span className="text-orange-700">{leaveStats.pending} leave request(s) pending</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-green-700">All leave requests processed</span>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Staff Holidays */}
      {upcomingStaffHolidays.length > 0 && (
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-green-600" />
              Your Upcoming Days Off ({upcomingStaffHolidays.length})
              <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">
                Next 30 Days
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingStaffHolidays.map((holiday, index) => (
                <div key={holiday.id} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-green-900">{holiday.name}</h4>
                            <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                              <Sparkles className="w-2 h-2 mr-1" />
                              Day Off
                            </Badge>
                          </div>
                          {holiday.description && (
                            <p className="text-sm text-green-700 mt-1">{holiday.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <div className="text-right">
                        <div className="text-sm font-medium text-green-900">
                          {new Date(holiday.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-green-600">
                          {Math.ceil((new Date(holiday.date) - new Date()) / (1000 * 60 * 60 * 24))} days away
                        </div>
                      </div>
                      <Badge variant="outline" className="text-green-700 border-green-300 capitalize text-xs">
                        {holiday.holiday_type}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 text-center">
                <Sparkles className="w-4 h-4 inline mr-1" />
                <strong>Staff Holidays:</strong> You have {upcomingStaffHolidays.length} upcoming day{upcomingStaffHolidays.length !== 1 ? 's' : ''} off in the next 30 days. Enjoy your well-deserved breaks!
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leave Application Modal */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply for Leave</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="leave_type">Leave Type</Label>
              <Select value={leaveForm.leave_type} onValueChange={(value) => setLeaveForm({...leaveForm, leave_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full Day">
                    <div className="flex items-center space-x-2">
                      <span>📅</span>
                      <div>
                        <div className="font-medium">Full Day</div>
                        <div className="text-xs text-gray-500">Complete day off</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="Half Day">
                    <div className="flex items-center space-x-2">
                      <span>🕐</span>
                      <div>
                        <div className="font-medium">Half Day</div>
                        <div className="text-xs text-gray-500">Morning/afternoon off</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="Short Leave">
                    <div className="flex items-center space-x-2">
                      <span>⏰</span>
                      <div>
                        <div className="font-medium">Short Leave</div>
                        <div className="text-xs text-gray-500">Brief absence</div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="from_date">From Date</Label>
                <Input
                  id="from_date"
                  type="date"
                  value={leaveForm.from_date}
                  onChange={(e) => setLeaveForm({...leaveForm, from_date: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="to_date">To Date</Label>
                <Input
                  id="to_date"
                  type="date"
                  value={leaveForm.to_date}
                  onChange={(e) => setLeaveForm({...leaveForm, to_date: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Duration (Auto-calculated)</Label>
              <div className="text-lg font-semibold text-green-600">
                {leaveForm.days_count > 0 ? `${leaveForm.days_count} day${leaveForm.days_count > 1 ? 's' : ''}` : 'Select dates'}
              </div>
            </div>

            <div>
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                placeholder="Enter reason for leave"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveModal(false)}>
              Cancel
            </Button>
            <Button onClick={submitLeaveRequest} disabled={loading}>
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Session Modal */}
      <Dialog open={showSessionModal} onOpenChange={setShowSessionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule a Session</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="subject_id">Subject</Label>
              <Select value={sessionForm.subject_id} onValueChange={(value) => setSessionForm({...sessionForm, subject_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map(subject => (
                    <SelectItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="batch_id">Batch</Label>
              <Select value={sessionForm.batch_id} onValueChange={(value) => setSessionForm({...sessionForm, batch_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select batch" />
                </SelectTrigger>
                <SelectContent>
                  {batches.map(batch => (
                    <SelectItem key={batch.id} value={batch.id}>
                      {batch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="classroom_id">Classroom</Label>
              <Select value={sessionForm.classroom_id} onValueChange={(value) => setSessionForm({...sessionForm, classroom_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select classroom" />
                </SelectTrigger>
                <SelectContent>
                  {classrooms.map(classroom => (
                    <SelectItem key={classroom.id} value={classroom.id}>
                      {classroom.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="session_date">Date</Label>
              <Input
                id="session_date"
                type="date"
                value={sessionForm.date}
                onChange={(e) => setSessionForm({...sessionForm, date: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_time">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={sessionForm.start_time}
                  onChange={(e) => setSessionForm({...sessionForm, start_time: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="end_time">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={sessionForm.end_time}
                  onChange={(e) => setSessionForm({...sessionForm, end_time: e.target.value})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSessionModal(false)}>
              Cancel
            </Button>
            <Button onClick={submitSessionRequest} disabled={loading}>
              Schedule Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;